import { useReactiveVar } from '@apollo/client'
import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { isReactiveContentTeamFromStudioPage } from 'api/graphQL/reactiveVars/workspaceContent'
import { workspaceToggles } from 'api/graphQL/reactiveVars/workspaceToggles'
import { PART_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useMemo } from 'react'
import { useElementItemStore } from '../ElementItemStore'
import type { DataNode } from '../types/types'
import { getActiveTabLabel } from '../utils'
import {
    useActiveTab,
    useArticlesLoading,
    useIsUnderMaintenance,
    useSelectedProjectElementNodeId,
} from '../Workspace2Store'

/**
 * Custom hook that encapsulates the logic for workspace editing permissions
 * @returns the list of of permission checks (conditional logic)
 */
export const usePermissionChecks = (
    /** The current tree data structure */
    treeData: DataNode[],
    /** The selected project element node from element tree */
    selectedProjectElementNode?: Partial<ProjectElementNode> | null
) => {
    const isUnderMaintenance = useIsUnderMaintenance()
    const activeTab = useActiveTab()
    const articlesLoading = useArticlesLoading()
    const selectedProjectElementNodeId = useSelectedProjectElementNodeId()
    const { isProductTypeProject, elementType } = useElementItemStore(
        (s) => s.elementItemValue
    )
    const { areSharedPropertiesVisible } = useReactiveVar(workspaceToggles)

    const isAssemblyTypeProject = useMemo(
        () =>
            selectedProjectElementNode?.elementType ===
            ProjectElementType.ASSEMBLY,
        [selectedProjectElementNode?.elementType]
    )

    // Checks if the selected element ID has changed from the Element Tree
    const isSelectedElementIdChanged =
        selectedProjectElementNodeId !== selectedProjectElementNode?.id

    const shouldAllowChange = useMemo(
        () => treeData.length > 0 && articlesLoading.length === 0,
        [treeData, articlesLoading]
    )

    const shouldAllowPTChange = useMemo(
        () =>
            isProductTypeProject &&
            getActiveTabLabel(activeTab) !== PART_TYPES.PRODUCTS &&
            !areSharedPropertiesVisible,
        [isProductTypeProject, activeTab, areSharedPropertiesVisible]
    )

    const isOMorSMMPage = isReactiveContentTeamFromStudioPage()?.value

    // Conditions when to use PPF (content items with DTV) request over DTV request only.
    const shouldUsePPFRequest =
        (selectedProjectElementNode?.isInProject === false && isOMorSMMPage) ||
        isSelectedElementIdChanged

    const showSharedPropertyLabel = useMemo(
        () =>
            (activeTab == 1 || activeTab == 3) &&
            elementType === ProjectElementType.PRODUCTTYPE,
        [activeTab, elementType]
    )
    return {
        isUnderMaintenance,
        shouldAllowChange,
        shouldAllowPTChange,
        isSelectedElementIdChanged,
        isProductTypeProject,
        isAssemblyTypeProject,
        shouldUsePPFRequest,
        showSharedPropertyLabel,
    }
}
